import { Styles } from '@types'
import { Alert, Form, Modal, Radio, Typography } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import bufferToFileDownload from '@utils/download'
import formRule from '@utils/formRule'

import PaymentAPI from '@apis/branch/payment'

import { RootState } from '@reducers/index'

import LocalDatePicker from '@components/atoms/LocalDatePicker'

type ExportType = 'summary' | 'detailed'
type SummaryCriteria = 'today' | 'month' | 'quarter'

const criteriaNote: Record<SummaryCriteria, { description: string; items: { label: string; text: string }[] }> = {
  today: {
    description: '오늘 날짜 기준으로 보증금 현황을 조회합니다.',
    items: [
      { label: '시작', text: '조회 날짜에 시작된 계약의 보증금' },
      { label: '종료', text: '조회 날짜에 종료된 계약의 보증금' },
      { label: '유지', text: '조회 날짜 기준 계약이 유지 중인 보증금' },
    ],
  },
  month: {
    description: '월 단위로 보증금 현황을 조회합니다.',
    items: [
      { label: '시작', text: '해당 월에 시작된 계약의 보증금' },
      { label: '종료', text: '해당 월에 종료되거나 조기 해지된 계약의 보증금' },
      { label: '유지', text: '해당 월 이전에 시작하여 해당 월 이후에 종료되는 계약의 보증금' },
    ],
  },
  quarter: {
    description: '분기 단위로 보증금 현황을 조회합니다.',
    items: [
      { label: '시작', text: '해당 분기에 시작된 계약의 보증금' },
      { label: '종료', text: '해당 분기에 종료되거나 조기 해지된 계약의 보증금' },
      { label: '유지', text: '해당 분기 이전에 시작하여 해당 분기 이후에 종료되는 계약의 보증금' },
    ],
  },
}

type Props = {
  hqId: number
  branchId: number
  onClose?: () => void
  onDone?: () => void
}

export default function DepositExportModal({ hqId, branchId, onClose, onDone }: Props): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [exportType, setExportType] = useState<ExportType>('summary')
  const [criteria, setCriteria] = useState<SummaryCriteria>('today')
  const [form] = useForm<{ contractRange: [Dayjs, Dayjs]; monthPeriod: Dayjs; quarterPeriod: Dayjs }>()
  const branchName = useSelector((state: RootState) => state.branch.name)

  const onOk = async () => {
    setLoading(true)
    try {
      const today = dayjs().format('YYMMDD')

      if (exportType === 'summary') {
        let period: string
        if (criteria === 'today') {
          period = dayjs().format('YYYY-MM-DD')
        } else if (criteria === 'month') {
          const { monthPeriod } = await form.validateFields(['monthPeriod'])
          period = dayjs(monthPeriod).format('YYYY-MM')
        } else {
          const { quarterPeriod } = await form.validateFields(['quarterPeriod'])
          const q = Math.ceil((dayjs(quarterPeriod).month() + 1) / 3)
          period = `${dayjs(quarterPeriod).format('YYYY')}-Q${q}`
        }

        const { data } = await PaymentAPI.exportDepositSummary({ hqId, branchId }, { criteria, period })
        bufferToFileDownload(`${branchName}_보증금요약_${today}.xlsx`, Buffer.from(data), 'xlsx')
      } else {
        const { contractRange } = await form.validateFields(['contractRange'])
        const from = dayjs(contractRange[0]).format('YYYY-MM-DD')
        const to = dayjs(contractRange[1]).format('YYYY-MM-DD')

        const { data } = await PaymentAPI.exportDepositPayments({ hqId, branchId }, { contractRange: [from, to] })
        bufferToFileDownload(`${branchName}_보증금_${today}.xlsx`, Buffer.from(data), 'xlsx')
      }

      if (onDone) onDone()
    } finally {
      setLoading(false)
    }
  }

  const initialValues = useMemo(
    () => ({
      contractRange: [dayjs().startOf('month'), dayjs().endOf('month')],
      monthPeriod: dayjs(),
      quarterPeriod: dayjs(),
    }),
    []
  )

  const onReset = () => {
    form.resetFields()
    setLoading(false)
    setExportType('summary')
    setCriteria('today')
  }

  useEffect(() => {
    onReset()
  }, [])

  const note = criteriaNote[criteria]

  return (
    <Modal visible onCancel={onClose} onOk={onOk} title="보증금 다운로드" okText="다운로드" confirmLoading={loading}>
      <Form form={form} layout="vertical" initialValues={initialValues}>
        <Form.Item label="형태">
          <Radio.Group value={exportType} onChange={(e) => setExportType(e.target.value)}>
            <Radio.Button value="summary">요약</Radio.Button>
            <Radio.Button value="detailed">상세</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {exportType === 'summary' && (
          <>
            <Form.Item label="기준">
              <Radio.Group value={criteria} onChange={(e) => setCriteria(e.target.value)}>
                <Radio.Button value="today">일 기준</Radio.Button>
                <Radio.Button value="month">월 기준</Radio.Button>
                <Radio.Button value="quarter">분기 기준</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Alert
              type="warning"
              showIcon
              style={styles.alert}
              message={note.description}
              description={
                <ul style={styles.noteList}>
                  {note.items.map(({ label, text }) => (
                    <li key={label}>
                      <Typography.Text strong>{label}: </Typography.Text>
                      {text}
                    </li>
                  ))}
                </ul>
              }
            />

            {criteria === 'today' && (
              <Form.Item label="조회기간" style={styles.periodItem}>
                <Typography.Text>{dayjs().format('YYYY-MM-DD')}</Typography.Text>
              </Form.Item>
            )}

            {criteria === 'month' && (
              <Form.Item label="조회기간" name="monthPeriod" rules={[formRule.required]} style={styles.periodItem}>
                <LocalDatePicker
                  picker="month"
                  size="large"
                  style={styles.datePicker}
                  allowClear={false}
                  inputReadOnly
                />
              </Form.Item>
            )}

            {criteria === 'quarter' && (
              <Form.Item label="조회기간" name="quarterPeriod" rules={[formRule.required]} style={styles.periodItem}>
                <LocalDatePicker
                  picker="quarter"
                  size="large"
                  style={styles.datePicker}
                  allowClear={false}
                  inputReadOnly
                />
              </Form.Item>
            )}
          </>
        )}

        {exportType === 'detailed' && (
          <Form.Item label="계약기간" name="contractRange" rules={[formRule.required]}>
            <LocalDatePicker.RangePicker size="large" style={styles.datePicker} allowClear={false} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}

const styles: Styles = {
  datePicker: { width: '100%' },
  alert: { marginBottom: 16 },
  noteList: { paddingLeft: 16, marginBottom: 0 },
  periodItem: { marginBottom: 0 },
}
